import * as yup from 'yup';
import { calculateAge, getFirstOfNextMonth } from '@/utils/dates/dates';
import { LAUFZEIT } from '@/components/form-elements/DurationSelection/types';
import { useClientStore } from '@/features/Clients';
import { showWertentwicklung } from '@/features/Sparten/shared/renderConditions/provident/sharedRenderConditions';
import { useInputStore } from '@/features/Sparten';

export const complexSelectionSchema = yup.object({
  gesellschaftsId: yup.number().required(),
  value: yup.string().required(),
});

export type ComplexSelection = yup.Asserts<typeof complexSelectionSchema>;

export const providentBaseInputSchema = yup.object({
  zahlweise: yup.string().required().default(''),
  versicherungsbeginn: yup.date().default(getFirstOfNextMonth()).required(),
  angenommeneWertentwicklung: yup
    .string()
    .default('')
    .when({
      is: () => showWertentwicklung.value,
      then: yup.string().required().default(''),
    }),
  beitragsdynamik: yup.string().required().default(''),
  beitragsendalter: yup
    .number()
    .min(10)
    .max(85)
    .integer()
    .required()
    .default(67),
  berufComplex: yup
    .array()
    .of(complexSelectionSchema)
    .min(1)
    .required()
    .default([]),
  laufzeitArt: yup
    .number()
    .oneOf([LAUFZEIT.ENDALTER, LAUFZEIT.JAHRE])
    .default(LAUFZEIT.ENDALTER),
  laufzeitInJahre: yup
    .number()
    .default(LAUFZEIT.ENDALTER)
    .when('laufzeitArt', {
      is: (val: LAUFZEIT) => val === LAUFZEIT.JAHRE,
      then: yup.number().test({
        name: 'isValid',
        message() {
          const vpAge = calculateAge(
            useClientStore().versichertePerson.data.geburtsdatum ?? new Date(),
            useInputStore().versicherungsbeginn,
          );
          return `Laufzeit darf ${86 - vpAge} Jahre nicht überschreiten`;
        },
        test(val) {
          const ctx: Record<string, Record<string, unknown>> | undefined =
            this.options.context;

          if (ctx) {
            const vpAge = calculateAge(
              useClientStore().versichertePerson.data.geburtsdatum ??
                new Date(),
              ctx.versicherungsbeginn as unknown as Date,
            );

            return val === undefined || val <= 86 - vpAge;
          }

          return false;
        },
      }),
    }),
  produktgruppeComplex: yup.array().of(complexSelectionSchema).default([]),
  tarifgruppeComplex: yup.array().of(complexSelectionSchema).default([]),
  ueberschusssystem: yup.string().required().default(''),
  versicherungsendalter: yup
    .number()
    .min(10)
    .max(85)
    .default(67)
    .integer()
    .required(),
  temporaryInputFields: yup.object({
    abweichendeBeitragszahlung: yup.boolean().default(false),
    abweichendesLeistungsendalter: yup.boolean().default(false),
  }),
});
